import { useRef } from "react";
import { TextField, Tooltip } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import toast from "@/lib/toast";

type FileChangeReason =
  | "invalid-type"
  | "invalid-size"
  | "removed"
  | "selected";

interface FileInputProps {
  field: any;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  paperclipIcon?: string;
  showAttachmentIcon?: boolean;
  accept?: string;
  onFileChange?: (file: File | null, reason?: FileChangeReason) => void;
  fileName?: string;
  maxSize?: number;
  hiddenTextField?: boolean;
}

const FileInput = ({
  field,
  label,
  required = false,
  disabled = false,
  error = false,
  helperText,
  paperclipIcon = "/assets/media/paperclip.svg",
  showAttachmentIcon = false,
  accept,
  onFileChange,
  fileName,
  maxSize,
  hiddenTextField,

}: FileInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const truncate = (str: string, max = 25) => str.length > max ? str.substring(0, max) + "..." : str;

  const fileValue = field?.value?.name || "";
  const hasFile = !!(fileName || fileValue);

  const getFileTypeMessage = (accept?: string) => {
    if (!accept) return "Invalid file type";

    return `Allowed file types: ${accept}`;
  };

  const formatFileSize = (size?: number) => {
    if (!size) return "";

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isAcceptedFile = (file: File) => {
    if (!accept) return true;

    const acceptedTypes = accept
      .split(",")
      .map((item) => item.trim().toLowerCase());

    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const mimeType = file.type.toLowerCase();

    return acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return fileExtension === type;
      }

      if (type.endsWith("/*")) {
        return mimeType.startsWith(type.replace("*", ""));
      }

      return mimeType === type;
    });
  };

  return (
    <div style={{ position: "relative" }}>
      {!hiddenTextField && (
        <Tooltip
          title={
            fileName
              ? (fileName.length > 20 ? fileName : "")
              : (fileValue.length > 20 ? fileValue : "")
          }
        >
          <TextField
            label={label}
            variant="standard"
            fullWidth
            required={required}
            disabled={disabled}
            value={
              fileName
                ? truncate(fileName)
                : fileValue
                  ? truncate(fileValue)
                  : ""
            }
            error={error}
            helperText={helperText}
            onClick={(e) => {
              const target = e.target as HTMLElement;

              if (target.closest(".file-input-adornment")) {
                return;
              }

              inputRef.current?.click();
            }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    className="file-input-adornment"
                  >
                    {hasFile ? (
                      <span
                        style={{
                          color: "red",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                        onClick={() => {
                          field.onChange(null);
                          onFileChange?.(null, "removed");
                          if (inputRef.current) {
                            inputRef.current.value = "";
                          }
                        }}
                      >
                        Remove
                      </span>
                    ) : (
                      showAttachmentIcon && (
                        <IconButton
                          size="small"
                          onClick={() => inputRef.current?.click()}
                        >
                          <img
                            src={paperclipIcon}
                            alt="attach"
                            width={15}
                            height={15}
                          />
                        </IconButton>
                      )
                    )}
                  </InputAdornment>
                )
              }
            }}
          />
        </Tooltip>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        hidden
        ref={inputRef}
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0] || null;

          if (!file) return;

          // File type validation
          if (!isAcceptedFile(file)) {
            toast.warning(getFileTypeMessage(accept));

            field.onChange(null);
            onFileChange?.(null, "invalid-type");

            if (inputRef.current) {
              inputRef.current.value = "";
            }

            return;
          }

          // Size validation
          if (maxSize && file.size > maxSize) {
            toast.warning(
              `File size should be less than ${formatFileSize(maxSize)}`
            );

            onFileChange?.(null, "invalid-size");
            field.onChange(null);

            if (inputRef.current) {
              inputRef.current.value = "";
            }

            return;
          }

          field.onChange(file);
          onFileChange?.(file, "selected");
        }}
      />
    </div>
  );
};

export default FileInput;
